import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Tag, Tooltip, Spin } from 'antd';
import { LoadingOutlined, PaperClipOutlined, CommentOutlined, CheckSquareOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import './style.css';
import { LIST_STATUS_META as STATUS_META, isResolved as isResolvedStatus } from './statusMeta';

const loadSpinIcon = <LoadingOutlined style={{ fontSize: 28, color: '#00b96b' }} spin />;

const systemKey = (incident) => {
  const cat = (incident.category || '').trim();
  return cat || '(bez systému)';
};

const IncidentsTable = ({ incidents, backlogIncidents, testIncidents, onRowClick, loading = false, groupBySystem = false }) => {
  const [companies, setCompanies] = useState([]);
  const [animKey, setAnimKey] = useState(0);
  const wasLoading = useRef(loading);

  useEffect(() => {
    if (wasLoading.current && !loading) {
      setAnimKey(k => k + 1);
    }
    wasLoading.current = loading;
  }, [loading]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/companies`)
      .then(response => {
        setCompanies(response.data);
      })
      .catch(() => {
        console.error('Nastala chyba pri načítaní spoločností!');
      });
  }, []);

  const getCompany = (alias) => {
    return companies.find(company => company.alias === alias) || {};
  };

  // DEPLOYED TO TEST (6) and Backlog (5) have their own sections
  const hasTestSection = Array.isArray(testIncidents);

  const activeRows = useMemo(
    () => (incidents || []).filter((inc) => Number(inc.status) !== 5 && !(hasTestSection && Number(inc.status) === 6)),
    [incidents, hasTestSection],
  );

  const backlogRows = useMemo(() => {
    if (Array.isArray(backlogIncidents)) return backlogIncidents;
    return (incidents || []).filter((inc) => Number(inc.status) === 5);
  }, [incidents, backlogIncidents]);

  const testRows = useMemo(() => (hasTestSection ? testIncidents : []), [hasTestSection, testIncidents]);

  const groupRows = (rows) => {
    if (!groupBySystem) return null;
    const map = {};
    rows.forEach(inc => {
      const key = systemKey(inc);
      if (!map[key]) map[key] = [];
      map[key].push(inc);
    });
    return Object.keys(map)
      .sort((a, b) => a.localeCompare(b, 'sk'))
      .map(name => ({ name, rows: map[name] }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const grouped = useMemo(() => groupRows(activeRows), [activeRows, groupBySystem]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groupedBacklog = useMemo(() => groupRows(backlogRows), [backlogRows, groupBySystem]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groupedTest = useMemo(() => groupRows(testRows), [testRows, groupBySystem]);

  const renderRow = (incident, index) => {
    const {
      id, niceid, text, priority, category, status, email, created, commentCount, attachmentCount, resolution,
    } = incident;

    const createdAt = created ? moment(created) : null;
    const timeFromNow = createdAt ? createdAt.fromNow() : '';
    const isOlderThanMonth = createdAt ? createdAt.isBefore(moment().subtract(1, 'month')) : false;
    const companyTag = (niceid || '').split('-')[0];
    const companyInfo = getCompany(companyTag);
    const isUrgent = String(priority) === '1';
    const hasAttachment = Number(attachmentCount) > 0;
    const hasComments = Number(commentCount) > 0;
    const statusMeta = STATUS_META[status] || STATUS_META[1];

    const getTagStyle = () => ({
      backgroundColor: companyInfo.background || '#485252',
      borderColor: companyInfo.border || '#677474',
      color: companyInfo.text || '#bdb8b8',
      margin: 0,
      fontSize: 11,
      lineHeight: '18px',
      padding: '0 6px',
    });

    const isResolved = isResolvedStatus(status);
    const isRejected = String(status) === '4';
    const isDone = isResolved || isRejected;

    return (
      <div
        key={id}
        className={`incident-row stagger-in${isDone ? ' incident-row-done' : ''}${isRejected ? ' incident-row-rejected' : ''}${groupBySystem ? ' incident-row--grouped' : ''}`}
        style={{ '--i': Math.min(index, 24) }}
        onClick={() => onRowClick(id)}
      >
        <span className="incident-tag"><Tag style={getTagStyle()}>{companyTag}</Tag></span>
        <span className="incident-id">{niceid}</span>
        {isUrgent && <span className="incident-urgent-dot" title="Urgentné" />}
        <span className="incident-title-wrap">
          <span className="incident-text" title={text}>{text}</span>
          {category && (
            <span className="incident-system">{String(category).toUpperCase()}</span>
          )}
        </span>
        <span className="incident-icons">
          {hasAttachment && (
            <Tooltip title="Príloha">
              <PaperClipOutlined className="incident-icon" />
            </Tooltip>
          )}
          {hasComments && (
            <Tooltip title={`${commentCount} komentárov`}>
              <CommentOutlined className="incident-icon" />
            </Tooltip>
          )}
          {resolution && String(resolution).trim() && (
            <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{resolution}</span>}>
              <CheckSquareOutlined className="incident-icon" />
            </Tooltip>
          )}
        </span>
        <span className="incident-status" style={{ color: statusMeta.color }}>{statusMeta.label}</span>
        <Tooltip title={email}>
          <span className="incident-email">{email}</span>
        </Tooltip>
        <span className={`incident-ago${isOlderThanMonth ? ' incident-ago-warn' : ''}`}>{timeFromNow}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="inc-list-loading">
        <div className="inc-list-spinner">
          <Spin indicator={loadSpinIcon} />
          <span className="inc-list-spinner-label">Načítavam požiadavky…</span>
        </div>
      </div>
    );
  }

  let rowIndex = 0;

  const renderGrouped = (groups, keyPrefix) => (
    groups.map((group) => {
      const groupIdx = rowIndex;
      rowIndex += 1;
      return (
        <div key={`${keyPrefix}-${group.name}`} className="inc-list-optgroup">
          <div
            className="inc-list-optgroup-label stagger-in"
            style={{ '--i': Math.min(groupIdx, 24) }}
          >
            {group.name}
            <span className="inc-list-optgroup-count">{group.rows.length}</span>
          </div>
          {group.rows.map((inc) => {
            const idx = rowIndex;
            rowIndex += 1;
            return renderRow(inc, idx);
          })}
        </div>
      );
    })
  );

  return (
    <div className="inc-list-stagger stagger-wrap" key={`${animKey}-${groupBySystem ? 'g' : 'f'}`}>
      {testRows.length > 0 && (
        <div className="inc-list-optgroup inc-list-test">
          <div
            className="inc-list-optgroup-label inc-list-test-label stagger-in"
            style={{ '--i': 0 }}
          >
            DEPLOYED TO TEST – na otestovanie
            <span className="inc-list-optgroup-count">{testRows.length}</span>
          </div>
          {groupBySystem && groupedTest
            ? renderGrouped(groupedTest, 'test')
            : testRows.map((inc) => {
              const idx = rowIndex;
              rowIndex += 1;
              return renderRow(inc, idx);
            })}
        </div>
      )}
      {groupBySystem && grouped
        ? renderGrouped(grouped, 'active')
        : activeRows.map((inc) => {
          const idx = rowIndex;
          rowIndex += 1;
          return renderRow(inc, idx);
        })}
      {backlogRows.length > 0 && (
        <div className="inc-list-optgroup inc-list-backlog">
          <div
            className="inc-list-optgroup-label inc-list-backlog-label stagger-in"
            style={{ '--i': Math.min(rowIndex, 24) }}
          >
            Backlog
            <span className="inc-list-optgroup-count">{backlogRows.length}</span>
          </div>
          {groupBySystem && groupedBacklog
            ? renderGrouped(groupedBacklog, 'backlog')
            : backlogRows.map((inc) => {
              const idx = rowIndex;
              rowIndex += 1;
              return renderRow(inc, idx);
            })}
        </div>
      )}
    </div>
  );
};

export default IncidentsTable;
